import { useIsMutating, useQuery } from '@tanstack/react-query';
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAddCartItem } from '@lib/api/hooks/cart/use-add-cart-item';
import { useClearStoreCart } from '@lib/api/hooks/cart/use-clear-store-cart';
import { useImportCart } from '@lib/api/hooks/cart/use-import-cart';
import { useRemoveCartItem } from '@lib/api/hooks/cart/use-remove-cart-item';
import { useUpdateCartItem } from '@lib/api/hooks/cart/use-update-cart-item';
import {
  cartOptions,
  productCartMutationKey,
  productCartOptions,
} from '@lib/api/options/cart';
import { CartGroup, CartItem, CartProductItem, CartRecord } from '@lib/types';

const CART_STORAGE_PREFIX = 'tcg-marketplace-cart';

interface CartContextValue {
  groups: CartGroup[];
  updatingQuantity: boolean;
  distinctItemCount: number;
  totalQuantity: number;
  totalPrice: number;
  selectCart: (accountId: string) => void;
  addItem: (item: Omit<CartItem, 'id' | 'quantity'>) => void;
  updateQuantity: (offerId: string, quantity: number) => void;
  removeItem: (offerId: string) => void;
  clearStore: (storeId: string) => void;
}

const CartContext = createContext<CartContextValue | null>(null);

const toCartItem = (record: CartRecord): CartItem => ({
  id: record.id,
  offerId: record.offer.id,
  cardId: record.offer.card.id,
  cardSlug: record.offer.card.slug,
  cardGameCode: record.offer.card.gameCode,
  cardName: record.offer.card.namePt || record.offer.card.nameEn,
  imageUrl: record.offer.imageUrl,
  printingCode: record.offer.printing.code,
  collectionName: record.offer.printing.collectionName,
  rarityName: record.offer.printing.rarityName,
  languageCode: record.offer.language.code,
  languageName: record.offer.language.name,
  condition: record.offer.condition,
  edition: record.offer.edition,
  unitPrice: Number(record.offer.unitPrice),
  promotionEndsAt: record.offer.promotionEndsAt,
  originalUnitPrice: record.offer.originalUnitPrice
    ? Number(record.offer.originalUnitPrice)
    : undefined,
  quantity: record.quantity,
  availableQuantity: record.offer.availableQuantity,
  store: {
    id: record.offer.store.id,
    name: record.offer.store.name,
    slug: record.offer.store.slug,
    buyoutLimit: record.offer.store.buyoutLimit,
    shippingMethods: record.offer.store.shippingMethods,
  },
});

const readLegacyItems = (accountId: string) => {
  try {
    const storageKey = `${CART_STORAGE_PREFIX}:${accountId}`;
    const storedValue = localStorage.getItem(storageKey);
    const parsedValue: unknown = storedValue ? JSON.parse(storedValue) : [];

    if (!Array.isArray(parsedValue)) {
      return [];
    }

    return parsedValue.flatMap((item) => {
      if (!item || typeof item !== 'object') {
        return [];
      }

      const inventoryItemId = Reflect.get(item, 'offerId');
      const quantity = Reflect.get(item, 'quantity');

      return typeof inventoryItemId === 'string' && typeof quantity === 'number'
        ? [{ inventoryItemId, quantity }]
        : [];
    });
  } catch {
    return [];
  }
};

const CartProvider = ({ children }: PropsWithChildren) => {
  const [accountId, setAccountId] = useState<string>();
  const importedAccounts = useRef(new Set<string>());

  const { data: records = [] } = useQuery(cartOptions(accountId));
  const { data: products = [] } = useQuery(productCartOptions(accountId));
  const { addCartItem } = useAddCartItem();
  const { clearStoreCart } = useClearStoreCart();
  const { importCart } = useImportCart();
  const { removeCartItem } = useRemoveCartItem();
  const { updateCartItem, updatingCartItem } = useUpdateCartItem();

  const updatingProducts =
    useIsMutating({ mutationKey: productCartMutationKey }) > 0;

  const items = useMemo(() => records.map(toCartItem), [records]);

  useEffect(() => {
    if (!accountId) {
      return;
    }

    if (importedAccounts.current.has(accountId)) {
      return;
    }

    importedAccounts.current.add(accountId);

    const legacyItems = readLegacyItems(accountId);

    if (legacyItems.length === 0) {
      return;
    }

    importCart(legacyItems)
      .then(() => {
        localStorage.removeItem(`${CART_STORAGE_PREFIX}:${accountId}`);
      })
      .catch(() => {
        importedAccounts.current.delete(accountId);
      });
  }, [accountId, importCart]);

  const addItem = useCallback(
    (item: Omit<CartItem, 'id' | 'quantity'>) => {
      addCartItem({ inventoryItemId: item.offerId, quantity: 1 }).catch(
        () => undefined,
      );
    },
    [addCartItem],
  );

  const updateQuantity = useCallback(
    (offerId: string, quantity: number) => {
      const item = items.find((cartItem) => cartItem.offerId === offerId);

      if (item) {
        updateCartItem({ id: item.id, quantity }).catch(() => undefined);
      }
    },
    [items, updateCartItem],
  );

  const removeItem = useCallback(
    (offerId: string) => {
      const item = items.find((cartItem) => cartItem.offerId === offerId);

      if (item) {
        removeCartItem(item.id).catch(() => undefined);
      }
    },
    [items, removeCartItem],
  );

  const clearStore = useCallback(
    (storeId: string) => {
      clearStoreCart(storeId).catch(() => undefined);
    },
    [clearStoreCart],
  );

  const cardGroups = useMemo(
    () =>
      Object.values(
        items.reduce<Record<string, CartGroup>>((result, item) => {
          const current = result[item.store.id] ?? {
            store: item.store,
            items: [],
            quantity: 0,
            subtotal: 0,
          };

          return {
            ...result,
            [item.store.id]: {
              ...current,
              items: [...current.items, item],
              quantity: current.quantity + item.quantity,
              subtotal: current.subtotal + item.unitPrice * item.quantity,
            },
          };
        }, {}),
      ),
    [items],
  );
  const groups = useMemo(() => {
    const merged = new Map<string, CartGroup & { products: CartProductItem[] }>(
      cardGroups.map((group) => [group.store.id, { ...group, products: [] }]),
    );
    products.forEach((item) => {
      const group = merged.get(item.store.id) ?? {
        store: item.store,
        items: [],
        products: [],
        quantity: 0,
        subtotal: 0,
      };
      group.products.push(item);
      group.quantity += item.quantity;
      group.subtotal += Number(item.offer.price) * item.quantity;
      merged.set(item.store.id, group);
    });
    return [...merged.values()];
  }, [cardGroups, products]);
  const totalQuantity =
    products.reduce((sum, item) => sum + item.quantity, 0) +
    items.reduce((total, item) => total + item.quantity, 0);
  const totalPrice =
    products.reduce(
      (sum, item) => sum + Number(item.offer.price) * item.quantity,
      0,
    ) +
    items.reduce((total, item) => total + item.quantity * item.unitPrice, 0);
  const value = useMemo(
    () => ({
      groups,
      updatingQuantity: updatingCartItem || updatingProducts,
      distinctItemCount: items.length + products.length,
      totalQuantity,
      totalPrice,
      selectCart: setAccountId,
      addItem,
      updateQuantity,
      removeItem,
      clearStore,
    }),
    [
      addItem,
      clearStore,
      groups,
      items.length,
      products.length,
      removeItem,
      totalPrice,
      totalQuantity,
      updateQuantity,
      updatingCartItem,
      updatingProducts,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error('useCart deve ser utilizado dentro de CartProvider.');
  }

  return context;
};

export default CartProvider;
